import { AlertMessage, BehindFlag } from '@/app/components'
import useThermostatDeviceSectionViewModel from '../useThermostatDeviceSectionViewModel'
import MenuCard, { MenuCardItem } from '@/components/MenuCard'
import HomeEstimationUpdateNotification from '@/app/components/HomeEstimations/Notification/HomeEstimationUpdateNotificiation'
import ThermostatSavingsChip from '@/app/pages/app/dashboard/sections/ThermostatSection/components/ThermostatCostSavings/ThermostatSavingsChip'
import { centsToDollars } from '@/utils/currency'
import { thermostatCostHistorySummaryCollection } from '@/reducers/thermostatCostHistorySummaryCollection'

export default function ThermostatSettingsTab({
  settings,
  settingsError,
  loading,
  navigateToEventTemperatures,
  navigateToPrecoolPreheatSettings,
  navigateToCostCategoryControls,
  hasCostCategories,
  costCategoryToggle,
  thermostatId,
  monthCostOfOptiwattAdjustmentsCents,
  isEcobee,
}: ReturnType<typeof useThermostatDeviceSectionViewModel>) {
  // Fetch cost history to check loading state
  const { isLoadingOrIdle: isCostHistoryLoading } =
    thermostatCostHistorySummaryCollection.useFetch({
      path: `thermostats/${thermostatId}/cost_history/summary`,
    })

  // Calculate month savings amount
  const monthSavingsAmount = centsToDollars(
    Math.abs(monthCostOfOptiwattAdjustmentsCents),
  )

  // Show chip only when toggle is "On" and savings are positive
  const shouldShowChip =
    costCategoryToggle.rightLabel === 'On' &&
    monthCostOfOptiwattAdjustmentsCents < 0 &&
    monthSavingsAmount > 0
  return (
    <div className="flex flex-col w-full gap-4">
      {settingsError ? (
        <AlertMessage variant="error">
          There was an error loading your thermostat schedule settings. Please
          try again later.
        </AlertMessage>
      ) : (
        <>
          <MenuCard
            icon="Settings"
            title="Device Settings"
            iconColor="purple-500"
          >
            <MenuCardItem
              id="thermostat-event-temperatures-menu-item"
              loading={loading}
              rightLabel=""
              label="Event Temperatures"
              onClick={navigateToEventTemperatures}
            />
            {/* One "Save Money" entry: the cost-category page when the plan
                supports cost category control, otherwise the TOU pre-cool page
                rebranded as Save Money. */}
            {hasCostCategories ? (
              <MenuCardItem
                id="thermostat-cost-category-controls"
                loading={loading}
                rightLabel={costCategoryToggle.rightLabel}
                label="Save Money"
                onClick={navigateToCostCategoryControls}
                chip={
                  shouldShowChip ? (
                    <ThermostatSavingsChip
                      monthSavingsAmount={monthSavingsAmount}
                      loading={isCostHistoryLoading || loading}
                    />
                  ) : undefined
                }
              />
            ) : (
              <BehindFlag flag="thermostat_pre_cool_settings">
                <MenuCardItem
                  id="thermostat-save-money-menu-item"
                  loading={loading}
                  rightLabel={
                    !isEcobee && settings?.pre_cool_enabled ? 'On' : 'Off'
                  }
                  label="Save Money"
                  onClick={navigateToPrecoolPreheatSettings}
                />
              </BehindFlag>
            )}
          </MenuCard>
          <HomeEstimationUpdateNotification
            from={`devices/thermostat/${thermostatId}`}
            hideAfterHomeEstimationSubmission={true}
          />
        </>
      )}
    </div>
  )
}
